import { Component, OnInit, OnDestroy, AfterViewChecked, HostListener, ChangeDetectorRef, ChangeDetectionStrategy, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Subscription } from 'rxjs';
import { GuidedTourService, TourState } from '../../services/guided-tour.service';
import { SnackbarService } from '../../services/snackbar.service';

@Component({
  selector: 'app-guided-tour-overlay',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule],
  templateUrl: './guided-tour-overlay.component.html',
  styleUrls: ['./guided-tour-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GuidedTourOverlayComponent implements OnInit, OnDestroy, AfterViewChecked {

  @ViewChild('tooltipEl') tooltipEl: ElementRef<HTMLElement>;

  state: TourState | null = null;
  showBackdropMessage = false;
  selectedRating = 0;
  hoverRating = 0;
  private _sub: Subscription;
  private _needsReposition = false;
  private _backdropTimeout: any;
  private _keydownHandler = (event: KeyboardEvent) => this._onKeydown(event);

  constructor(
    private _tourService: GuidedTourService,
    private _cdr: ChangeDetectorRef,
    private _snackbar: SnackbarService
  ) {}

  ngOnInit(): void {
    this._sub = this._tourService.state$.subscribe(state => {
      this.state = state;
      this._needsReposition = true;
      this._cdr.markForCheck();
    });

    // Capture phase — przechwytuje klawisze zanim mat-menu je skonsumuje
    document.addEventListener('keydown', this._keydownHandler, true);
  }

  ngAfterViewChecked(): void {
    if (this._needsReposition && this.tooltipEl?.nativeElement) {
      this._needsReposition = false;
      this._repositionTooltip();
    }
  }

  ngOnDestroy(): void {
    this._sub?.unsubscribe();
    document.removeEventListener('keydown', this._keydownHandler, true);
  }

  @HostListener('window:resize')
  onResize(): void {
    if (this.state?.active) {
      this._needsReposition = true;
      this._cdr.markForCheck();
    }
  }

  private _onKeydown(event: KeyboardEvent): void {
    if (!this.state?.active || this.isTransitioning || this.isSliding) return;

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!this.isLastStep) this.next();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!this.isFirstStep) this.prev();
    } else if (event.key === 'Escape') {
      // Pozwól syntetycznym Escape (z _closeOpenMenu) dotrzeć do mat-menu
      if (!event.isTrusted) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.skip();
    }
  }

  get isActive(): boolean {
    return !!this.state?.active;
  }

  get isTransitioning(): boolean {
    return !!this.state?.transitioning;
  }

  get isSliding(): boolean {
    return !!this.state?.sliding;
  }

  get step() {
    if (!this.state) return null;
    return this.state.steps[this.state.currentStep];
  }

  get isFirstStep(): boolean {
    return this.state?.currentStep === 0;
  }

  get isLastStep(): boolean {
    if (!this.state) return false;
    return this.state.currentStep === this.state.steps.length - 1;
  }

  get isLastSlide(): boolean {
    return this.isLastStep && !this.step?.isHelpStep;
  }

  get isRatingStep(): boolean {
    return !!this.step?.isRatingStep;
  }

  get isPreRatingStep(): boolean {
    if (!this.state) return false;
    const nextStep = this.state.steps[this.state.currentStep + 1];
    return !!nextStep?.isRatingStep;
  }

  get isHelpStep(): boolean {
    return !!this.step?.isHelpStep;
  }

  get stepCounter(): string {
    if (!this.state) return '';
    return `${this.state.currentStep + 1} / ${this.state.steps.length}`;
  }

  get actionText(): string {
    return this.step?.actionText || 'Dalej';
  }

  /** CSS box-shadow tworzy "dziurę" w overlay */
  get highlightStyle(): { [key: string]: string } | null {
    const rect = this.state?.targetRect;
    if (!rect) return null;

    const padding = 8;
    return {
      top: `${rect.top - padding}px`,
      left: `${rect.left - padding}px`,
      width: `${rect.width + padding * 2}px`,
      height: `${rect.height + padding * 2}px`
    };
  }

  /** Repozycjonowanie tooltipa po renderze — clamp do viewportu, unikanie zasłonięcia targetu */
  private _repositionTooltip(): void {
    const el = this.tooltipEl?.nativeElement;
    if (!el) return;

    const rect = this.state?.targetRect;

    // Brak targetu — centruj na ekranie (ale unikaj otwartego menu)
    if (!rect) {
      const menuPanel = document.querySelector('.cdk-overlay-container .mat-mdc-menu-panel') as HTMLElement;
      if (menuPanel) {
        // Menu otwarte — pozycjonuj obok menu zamiast na środku
        el.style.transform = 'none';
        const menuRect = menuPanel.getBoundingClientRect();
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const tt = el.getBoundingClientRect();
        const ttW = tt.width;
        const ttH = tt.height;
        const gap = 16;
        const edgePad = 12;

        let top = menuRect.top + menuRect.height / 2 - ttH / 2;
        let left: number;

        // Preferuj lewą stronę menu (menu zazwyczaj w prawym górnym rogu)
        if (menuRect.left >= ttW + gap) {
          left = menuRect.left - gap - ttW;
        } else if (vw - menuRect.right >= ttW + gap) {
          left = menuRect.right + gap;
        } else {
          left = vw / 2 - ttW / 2;
          top = menuRect.bottom + gap;
        }

        left = Math.max(edgePad, Math.min(left, vw - edgePad - ttW));
        top = Math.max(edgePad, Math.min(top, vh - edgePad - ttH));

        el.style.top = `${top}px`;
        el.style.left = `${left}px`;
        el.style.right = 'auto';
        el.style.bottom = 'auto';
      } else {
        el.style.transform = 'translate(-50%, -50%)';
        el.style.top = '50%';
        el.style.left = '50%';
      }
      return;
    }

    // Ma target — usuń transform i pozycjonuj w px
    el.style.transform = 'none';

    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const tt = el.getBoundingClientRect();
    const ttW = tt.width;
    const ttH = tt.height;
    const gap = 16;
    const edgePad = 12;
    const highlightPad = 8;

    // Jeśli menu jest otwarte, unikaj całego panelu menu (nie tylko targetu)
    const menuPanel = document.querySelector('.cdk-overlay-container .mat-mdc-menu-panel') as HTMLElement;
    const avoidRect = menuPanel ? this._mergeRects(rect, menuPanel.getBoundingClientRect()) : rect;

    // Dostępna przestrzeń wokół strefy do uniknięcia
    const spaceBelow = vh - avoidRect.bottom - highlightPad;
    const spaceAbove = avoidRect.top - highlightPad;
    const spaceRight = vw - avoidRect.right - highlightPad;
    const spaceLeft = avoidRect.left - highlightPad;

    let top: number;
    let left: number;

    // Preferowana pozycja: pod strefą
    if (spaceBelow >= ttH + gap) {
      top = avoidRect.bottom + gap;
      left = rect.left + rect.width / 2 - ttW / 2;
    }
    // Nad strefą
    else if (spaceAbove >= ttH + gap) {
      top = avoidRect.top - gap - ttH;
      left = rect.left + rect.width / 2 - ttW / 2;
    }
    // Po prawej
    else if (spaceRight >= ttW + gap) {
      top = rect.top + rect.height / 2 - ttH / 2;
      left = avoidRect.right + gap;
    }
    // Po lewej
    else if (spaceLeft >= ttW + gap) {
      top = rect.top + rect.height / 2 - ttH / 2;
      left = avoidRect.left - gap - ttW;
    }
    // Fallback: pod strefą (clamp za chwilę)
    else {
      top = avoidRect.bottom + gap;
      left = rect.left + rect.width / 2 - ttW / 2;
    }

    // Clamp do viewportu
    if (left + ttW > vw - edgePad) {
      left = vw - edgePad - ttW;
    }
    if (left < edgePad) {
      left = edgePad;
    }
    if (top + ttH > vh - edgePad) {
      top = vh - edgePad - ttH;
    }
    if (top < edgePad) {
      top = edgePad;
    }

    // Ustaw finalne pozycje px
    el.style.top = `${top}px`;
    el.style.left = `${left}px`;
    el.style.right = 'auto';
    el.style.bottom = 'auto';
  }

  /** Łączy dwa recty w minimalny rect obejmujący oba */
  private _mergeRects(a: DOMRect, b: DOMRect): { top: number; bottom: number; left: number; right: number } {
    return {
      top: Math.min(a.top, b.top),
      bottom: Math.max(a.bottom, b.bottom),
      left: Math.min(a.left, b.left),
      right: Math.max(a.right, b.right)
    };
  }

  next(): void {
    this._tourService.next();
  }

  prev(): void {
    this._tourService.prev();
  }

  skip(): void {
    this._tourService.skipToHelp();
  }

  finishTour(): void {
    if (this.isRatingStep && this.selectedRating) {
      this._launchConfetti();
      setTimeout(() => {
        this._snackbar.showSnackBar('Dziękujemy za Twoją opinię! 🎉', 'success', 4000);
      }, 600);
    }
    this._tourService.finish(this.selectedRating || null);
  }

  /** Animacja konfetti po ocenie */
  private _launchConfetti(): void {
    const canvas = document.createElement('canvas');
    canvas.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:99999';
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    document.body.appendChild(canvas);
    const ctx = canvas.getContext('2d')!;

    const colors = ['#4caf50', '#ff9800', '#2196f3', '#e91e63', '#9c27b0', '#ffeb3b', '#00bcd4'];
    const particles: { x: number; y: number; vx: number; vy: number; size: number; color: string; rotation: number; rotSpeed: number; shape: number; opacity: number }[] = [];

    // Generuj cząstki z dwóch źródeł (lewa i prawa strona w górze)
    for (let i = 0; i < 150; i++) {
      const fromLeft = Math.random() < 0.5;
      particles.push({
        x: fromLeft ? canvas.width * 0.15 : canvas.width * 0.85,
        y: canvas.height * 0.2,
        vx: (fromLeft ? 1 : -1) * (Math.random() * 6 + 2),
        vy: -(Math.random() * 12 + 4),
        size: Math.random() * 8 + 4,
        color: colors[Math.floor(Math.random() * colors.length)],
        rotation: Math.random() * 360,
        rotSpeed: (Math.random() - 0.5) * 10,
        shape: Math.floor(Math.random() * 3),
        opacity: 1
      });
    }

    let frame = 0;
    const maxFrames = 120;
    const gravity = 0.25;
    const friction = 0.99;

    const animate = () => {
      frame++;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (const p of particles) {
        p.vy += gravity;
        p.vx *= friction;
        p.x += p.vx;
        p.y += p.vy;
        p.rotation += p.rotSpeed;
        if (frame > maxFrames * 0.6) {
          p.opacity = Math.max(0, p.opacity - 0.03);
        }

        ctx.save();
        ctx.globalAlpha = p.opacity;
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.fillStyle = p.color;

        if (p.shape === 0) {
          // Prostokąt
          ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
        } else if (p.shape === 1) {
          // Kółko
          ctx.beginPath();
          ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
          ctx.fill();
        } else {
          // Trójkąt
          ctx.beginPath();
          ctx.moveTo(0, -p.size / 2);
          ctx.lineTo(p.size / 2, p.size / 2);
          ctx.lineTo(-p.size / 2, p.size / 2);
          ctx.closePath();
          ctx.fill();
        }

        ctx.restore();
      }

      if (frame < maxFrames) {
        requestAnimationFrame(animate);
      } else {
        canvas.remove();
      }
    };

    requestAnimationFrame(animate);
  }

  setRating(star: number): void {
    this.selectedRating = star;
  }

  getRatingEmoji(rating: number): string {
    const emojis: Record<number, string> = { 0: '⭐', 1: '😞', 2: '😕', 3: '😐', 4: '😊', 5: '🤩' };
    return emojis[rating] || '⭐';
  }

  connectKsef(): void {
    this._tourService.finishAndConnectKsef();
  }

  onBackdropClick(event: MouseEvent): void {
    event.stopPropagation();
    clearTimeout(this._backdropTimeout);
    this.showBackdropMessage = true;
    this._cdr.markForCheck();
    this._backdropTimeout = setTimeout(() => {
      this.showBackdropMessage = false;
      this._cdr.markForCheck();
    }, 3000);
  }
}
